import { useState, useEffect, useCallback } from 'react';
import type { StudentPromptsData, AnalyticsPeriod } from '../types/dashboard';

interface UseStudentPromptsResult {
  data: StudentPromptsData | null;
  loading: boolean;
  error: string | null;
  period: AnalyticsPeriod;
  setPeriod: (period: AnalyticsPeriod) => void;
  refresh: () => Promise<void>;
}

/**
 * Hook to fetch student-created prompts for teacher dashboard
 * Shows what custom lessons students are creating and pronunciation words they're practicing
 */
export function useStudentPrompts(
  teacherId: string | undefined,
  externalPeriod?: AnalyticsPeriod,
  isActive: boolean = true
): UseStudentPromptsResult {
  const [data, setData] = useState<StudentPromptsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [internalPeriod, setInternalPeriod] = useState<AnalyticsPeriod>('week');

  // Allow parent to control period, fallback to internal state
  const period = externalPeriod ?? internalPeriod;

  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

  const fetchPrompts = useCallback(async () => {
    if (!teacherId || !isActive) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/prompts/teacher/${teacherId}?period=${period}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch prompts: ${response.status}`);
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching student prompts:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch prompts');
      // Set empty data on error so UI can still render
      setData({
        prompts: [],
        summary: { customLessons: 0, pronunciationSessions: 0, uniqueStudents: 0 }
      });
    } finally {
      setLoading(false);
    }
  }, [teacherId, period, isActive, API_BASE_URL]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  return {
    data,
    loading,
    error,
    period,
    setPeriod: setInternalPeriod,
    refresh: fetchPrompts,
  };
}
